import React from 'react'

import { inject, observer } from 'mobx-react'

import { Resource } from '~/client/src/desktop/components/Resource'
import { ResourceExportDialog } from '~/client/src/desktop/components/ResourceExportDialog/ResourceExportDialog'
import { ResourcesImportDialog } from '~/client/src/desktop/components/ResourcesImportDialog/ResourcesImportDialog'
import DesktopInitialState from '~/client/src/desktop/stores/DesktopInitialState'
import { LocationsProvider } from '~/client/src/desktop/views/Locations/LocationsProvider'
import { ResourceTypeId } from '~/client/src/shared/constants/resourceTypes'
import { useLocationResourceStore } from '~/client/src/shared/hooks/useLocationResourceStore'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import { FileUploadingStore } from '~/client/src/shared/stores/domain/FileUploading.store'

import DeletedParentLocationsBanner from './DeletedParentLocationsBanner'

function LocationsObjects({
  state,
  fileUploadingStore,
}: {
  state?: DesktopInitialState
  fileUploadingStore?: FileUploadingStore
}) {
  const projectId = state?.activeProject?.id || ''
  const userId = state?.user?.id || ''
  const { schema } = useLocationResourceStore(projectId || undefined)

  const [isImportDialogOpen, setIsImportDialogOpen] = React.useState(false)
  const [isExportDialogOpen, setIsExportDialogOpen] = React.useState(false)

  if (!projectId || !userId || !state) {
    return null
  }

  return (
    <div className="locations tw:h-full tw:flex tw:flex-col">
      <LocationsProvider projectId={projectId} userId={userId} state={state}>
        <div className="multi-grid-container tw:relative tw:overflow-visible tw:flex tw:flex-col tw:h-full tw:min-h-0">
          <div className="tw:grow-0">
            <DeletedParentLocationsBanner
              projectId={projectId}
              userId={userId}
            />
            <Resource.Header>
              <Resource.Search />
              <Resource.Separator />
              <Resource.Filters />
              <Resource.Actions>
                <Resource.DisplayOptions />
              </Resource.Actions>
            </Resource.Header>
          </div>
          <div className="tw:flex-1 tw:min-h-0">
            <Resource.Content>
              <Resource.ActionBar>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Resource.CollapseToggle />
                  <Resource.RecordCount
                    label={c => Localization.translator.xLocations(c)}
                  />
                  <Resource.DeleteButton
                    confirmationLabel={c =>
                      Localization.translator.confirmDeletionOfLocations(c)
                    }
                  />
                  <Resource.BulkEditButton />
                </div>
                <div className="tw:flex tw:items-center tw:gap-2">
                  <Resource.ImportButton
                    onClick={() => setIsImportDialogOpen(true)}
                  />
                  <Resource.ExportButton
                    onClick={() => setIsExportDialogOpen(true)}
                  />
                  <Resource.CreateButton />
                </div>
              </Resource.ActionBar>
              <Resource.Table />
            </Resource.Content>
          </div>
        </div>
        <Resource.EditPanel />
        <Resource.BulkEditPanel />
      </LocationsProvider>
      <ResourceExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        projectId={projectId}
        resourceTypeId={ResourceTypeId.LOCATIONS}
        properties={schema.properties}
        loading={schema.loading}
        propertiesLabel={Localization.translator.locationProperties}
      />
      {isImportDialogOpen && (
        <ResourcesImportDialog
          isOpen={isImportDialogOpen}
          onClose={() => setIsImportDialogOpen(false)}
          projectId={projectId}
          resourceTypeId={ResourceTypeId.LOCATIONS}
          fileUploadingStore={fileUploadingStore}
          resourceSchema={schema}
        />
      )}
    </div>
  )
}

export default inject('state', 'fileUploadingStore')(observer(LocationsObjects))
